import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { getRegistrationData } from '../data/getRegistrationData';
import { formOptions } from './formOptions';
import { messages, type Messages } from './messages';
import type { FormOptions } from './formOptions';
import { LANG_STORAGE_KEY, type Lang } from './types';

interface LanguageContextValue {
  lang: Lang;
  setLang: (lang: Lang) => void;
  m: Messages;
  fo: FormOptions;
  rd: ReturnType<typeof getRegistrationData>;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

function readStoredLang(): Lang {
  try {
    const stored = localStorage.getItem(LANG_STORAGE_KEY);
    if (stored === 'en' || stored === 'vi') return stored;
  } catch {
    // ignore
  }
  return 'vi';
}

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLangState] = useState<Lang>(readStoredLang);

  const setLang = useCallback((next: Lang) => {
    setLangState(next);
    try {
      localStorage.setItem(LANG_STORAGE_KEY, next);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang === 'en' ? 'en' : 'vi';
  }, [lang]);

  const value = useMemo<LanguageContextValue>(
    () => ({
      lang,
      setLang,
      m: messages[lang],
      fo: formOptions[lang],
      rd: getRegistrationData(lang),
    }),
    [lang, setLang]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage(): LanguageContextValue {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return ctx;
}